import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { pt } from './locales/pt.js';
import { en } from './locales/en.js';

const I18nContext = createContext(null);
const STORAGE_KEY = 'tc_lang';

function detectLangFromPath() {
  if (typeof window === 'undefined') return null;
  if (window.location.pathname.startsWith('/en/')) return 'en';
  if (window.location.pathname === '/en') return 'en';
  return null;
}

function detectLang() {
  if (typeof window === 'undefined') return 'pt';
  
  const pathLang = detectLangFromPath();
  if (pathLang) return pathLang;

  const savedLang = localStorage.getItem(STORAGE_KEY);
  if (savedLang) return savedLang;
  
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('en')) return 'en';
  
  return 'pt';
}

export function I18nProvider({ children }) {
  const location = useLocation();
  // Detecta o idioma de forma síncrona, antes da primeira pintura,
  // para evitar que o texto mude de tamanho depois de carregado (CLS).
  const [lang, setLang] = useState(() => detectLang());
  const [isLoaded, setIsLoaded] = useState(true);

  // Re-detecta o idioma caso algo tenha mudado entre a primeira renderização
  // e a montagem completa do componente (raro, mas mantido por segurança).
  useEffect(() => {
    const initialLang = detectLang();
    setLang((prev) => (prev !== initialLang ? initialLang : prev));
  }, []);

  // React to URL changes: update language when the /en prefix changes
  useEffect(() => {
    if (!isLoaded) return;
    const pathLang = detectLangFromPath() || 'pt';
    setLang((prev) => (prev !== pathLang ? pathLang : prev));
  }, [location.pathname, isLoaded]);

  // Persist language and sync document lang
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    }
  }, [lang, isLoaded]);

  // Fallback t() and dict for compatibility with existing utils like getTranslatedCategories
  const dict = lang === 'en' ? en : pt;
  const t = (keyPath, params = {}) => {
    const keys = keyPath.split('.');
    let value = dict;
    for (const key of keys) {
      if (value === undefined || value === null) break;
      value = value[key];
    }
    
    let result = value !== undefined && value !== null ? value : keyPath;
    
    // Simple interpolation logic for params like { name: 'Something' }
    if (typeof result === 'string' && Object.keys(params).length > 0) {
      Object.keys(params).forEach(key => {
        result = result.replace(new RegExp(`{${key}}`, 'g'), params[key]);
      });
    }
    
    return result;
  };

  if (!isLoaded) {
    return null; 
  }

  return (
    <I18nContext.Provider value={{ lang, setLang, t, dict }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}